import type { ElementType, ReactNode } from 'react';

import { useFusionTheme, useGeneratedClasses } from '../hooks';

import styles from './Flex.module.scss';
import type {
  alignContentOptions,
  alignItemsOptions,
  alignSelfOptions,
  directionOptions,
  displayOptions,
  justifyContentOptions,
  justifySelfOptions,
  marginOptions,
  positionOptions,
  sizingOptions,
  spacingOptions,
  wrapOptions
} from './constants';

interface ResponsiveProp<T> {
  base?: T;
  xxs?: T;
  xs?: T;
  sm?: T;
  md?: T;
  lg?: T;
  xl?: T;
}

export type Responsive<T> = T | ResponsiveProp<T>;

type Display = (typeof displayOptions)[number];
type Spacing = (typeof spacingOptions)[number];
type Margin = (typeof marginOptions)[number];
type Sizing = (typeof sizingOptions)[number];
type JustifyContent = (typeof justifyContentOptions)[number];
type AlignContent = (typeof alignContentOptions)[number];
type AlignItems = (typeof alignItemsOptions)[number];
type AlignSelf = (typeof alignSelfOptions)[number];
type JustifySelf = (typeof justifySelfOptions)[number];
type FlexDirection = (typeof directionOptions)[number];
type FlexWrap = (typeof wrapOptions)[number];
type Position = (typeof positionOptions)[number];
type PositioningValue = number | string;

export interface Props {
  style?: React.CSSProperties;
  children?: ReactNode;
  className?: string;
  tagName?: ElementType;
  display?: Responsive<Display>;
  direction?: Responsive<FlexDirection>;
  wrap?: Responsive<FlexWrap | boolean>;
  justifyContent?: Responsive<JustifyContent>;
  alignContent?: Responsive<AlignContent>;
  alignItems?: Responsive<AlignItems>;
  gap?: Responsive<Spacing>;
  columnGap?: Responsive<Spacing>;
  rowGap?: Responsive<Spacing>;
  grow?: Responsive<number>;
  shrink?: Responsive<number>;
  basis?: Responsive<Sizing>;
  alignSelf?: Responsive<AlignSelf>;
  justifySelf?: Responsive<JustifySelf>;
  order?: Responsive<number>;
  width?: Responsive<Sizing>;
  minWidth?: Responsive<Sizing>;
  maxWidth?: Responsive<Sizing>;
  height?: Responsive<Sizing>;
  minHeight?: Responsive<Sizing>;
  maxHeight?: Responsive<Sizing>;
  margin?: Responsive<Margin>;
  marginStart?: Responsive<Margin>;
  marginEnd?: Responsive<Margin>;
  marginTop?: Responsive<Margin>;
  marginBottom?: Responsive<Margin>;
  marginX?: Responsive<Margin>;
  marginY?: Responsive<Margin>;
  padding?: Responsive<Spacing>;
  paddingStart?: Responsive<Spacing>;
  paddingEnd?: Responsive<Spacing>;
  paddingTop?: Responsive<Spacing>;
  paddingBottom?: Responsive<Spacing>;
  paddingX?: Responsive<Spacing>;
  paddingY?: Responsive<Spacing>;
  position?: Responsive<Position>;
  top?: PositioningValue;
  bottom?: PositioningValue;
  left?: PositioningValue;
  right?: PositioningValue;
  zIndex?: number | 'auto';
}

const transformName = (name: string) => {
  const propertiesMap: Record<string, string> = {
    direction: 'flexDirection',
    grow: 'flexGrow',
    shrink: 'flexShrink',
    basis: 'flexBasis',
    wrap: 'flexWrap'
  };

  return propertiesMap[name] || name;
};

const transformValue = <T,>(name: string, value: T) => {
  if (
    ['flexBasis', 'width', 'minWidth', 'maxWidth', 'height', 'minHeight', 'maxHeight'].includes(
      name
    )
  ) {
    return parseDimension(value);
  }

  if (name === 'flexWrap') {
    return parseFlexWrap(value);
  }

  if (['top', 'bottom', 'left', 'right'].includes(name)) {
    return parsePositionValue(value);
  }

  return value;
};

const parseDimension = <T,>(value: T) => String(value).replace('%', '-per');

const parseFlexWrap = <T,>(value: T) => {
  if (typeof value === 'boolean') {
    return value ? 'wrap' : 'nowrap';
  }
  return value;
};

const parsePositionValue = <T,>(value: T) => {
  // assume pixels if no other unit was specified
  // this is only needed in case the prop value was passed as a string: top="100" instead of number top={100}
  if (typeof value === 'string' && /^-?\d+$/.test(value)) {
    return `${value}px`;
  }
  return value;
};

const Flex = ({
  children,
  className,
  style,
  tagName: Tag = 'div',
  top,
  bottom,
  left,
  right,
  zIndex,
  ...classProps
}: Props) => {
  const { addThemeClassName } = useFusionTheme();
  const classes = useGeneratedClasses(classProps, styles, { transformName, transformValue });
  const inlineStyleProps = { top, bottom, left, right, zIndex };

  const inlineStyles = Object.fromEntries(
    Object.entries(inlineStyleProps)
      .filter(([name, value]) => value !== undefined)
      .map(([name, value]) => [name, transformValue(name, value)])
  );

  return (
    <Tag
      style={{
        ...style,
        ...inlineStyles
      }}
      className={addThemeClassName(styles.container, className, classes)}
    >
      {children}
    </Tag>
  );
};

export default Flex;
